"use client";

/**
 * P2-03 · Breadcrumb trail island. Renders the ordered `<ol>`; owns the mobile
 * overflow behaviour only. Deep trails (≥ 4 crumbs) collapse the middle behind
 * an expandable ellipsis on mobile so the trail never wraps; on desktop (`lg`)
 * every crumb is always shown and long labels truncate with a title tooltip.
 * The current page (last crumb) is text with `aria-current="page"`, never a
 * link. SSR renders the full list, so the trail is present without JS — the
 * ellipsis just re-hides the middle on small screens after hydration.
 */
import { useState } from "react";

import { LocaleLink, useTranslator } from "~/i18n/provider";
import type { Crumb } from "~/lib/breadcrumbs";
import { cn } from "~/lib/utils";

/** Collapse the middle on mobile only once the trail is this deep. */
const COLLAPSE_MIN_CRUMBS = 4;

function Separator() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3.5 w-3.5 shrink-0 text-fg-muted/60"
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

function CrumbLabel({ label }: { label: string }) {
  return (
    <span className="max-w-[12rem] truncate" title={label}>
      {label}
    </span>
  );
}

export function BreadcrumbTrail({ items }: { items: Crumb[] }) {
  const t = useTranslator();
  const [expanded, setExpanded] = useState(false);

  const first = items[0]!;
  const current = items[items.length - 1]!;
  const middle = items.slice(1, -1);
  const collapsible = items.length >= COLLAPSE_MIN_CRUMBS;
  // On mobile, hide the middle until the ellipsis is expanded.
  const hideMiddleOnMobile = collapsible && !expanded;

  return (
    <ol className="flex flex-nowrap items-center gap-1.5 text-sm text-fg-muted">
      <li className="flex shrink-0 items-center gap-1.5">
        <LocaleLink
          href={first.href}
          className="rounded-sm hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
        >
          <CrumbLabel label={first.label} />
        </LocaleLink>
      </li>

      {collapsible && !expanded && (
        <li className="flex shrink-0 items-center gap-1.5 lg:hidden">
          <Separator />
          <button
            type="button"
            onClick={() => setExpanded(true)}
            aria-label={t("nav.showAllBreadcrumbs")}
            className="rounded-sm px-0.5 hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            …
          </button>
        </li>
      )}

      {middle.map((crumb) => (
        <li
          key={crumb.href}
          className={cn(
            "shrink-0 items-center gap-1.5",
            hideMiddleOnMobile ? "hidden lg:flex" : "flex",
          )}
        >
          <Separator />
          <LocaleLink
            href={crumb.href}
            className="rounded-sm hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            <CrumbLabel label={crumb.label} />
          </LocaleLink>
        </li>
      ))}

      <li className="flex min-w-0 items-center gap-1.5">
        <Separator />
        <span className="truncate font-medium text-fg" aria-current="page">
          {current.label}
        </span>
      </li>
    </ol>
  );
}
